import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, DatePicker, Form } from 'antd';
import useEhrConnections from 'hooks/useEhrConnections';
import useEhrOptions from 'hooks/useEhrOptions';
import { fetchOnboardingVo } from '../../billingReviewData';
import SectionHeader from '../../components/SectionHeader';
import CardRadioGroup from '../../components/CardRadioGroup';
import HiddenFormField from '../../components/HiddenFormField';
import EhrBillingCheckList from '../../components/EhrBillingCheckList';
import {
    EhrConnectFlow,
    EhrConnectModal,
    PF_SETUP_LABEL,
    connectionLost,
    isPfSettingUp,
    neverConnected,
    notConnectedCopy,
} from 'components/EhrConnect';
import s from '../../s.module.less';
import sc from './s.module.less';

const InfoIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="10" fill="currentColor" />
        <circle cx="12" cy="8" r="1.25" fill="#fff" />
        <path d="M12 11.5v5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

// 患者范围 / 起始时间的选项与字段组件：向导 Step 6 与 settings 的 EhrScopeSettings 共用
export const SCOPE_OPTIONS = [
    {
        value: 'all',
        title: 'All patients',
        recommended: true,
        desc: 'Klarity processes claims for every billable encounter.',
    },
    {
        value: 'tagged',
        title: 'Only tagged patients',
        desc: 'Klarity only processes claims for patients you tag in your EHR.',
    },
];

export const START_OPTIONS = [
    {
        value: 'today',
        title: 'From today',
        tag: 'Default',
        desc: 'Only encounters from today onward.',
    },
    {
        value: 'specific',
        title: 'From a specific date',
        desc: 'Processes all unbilled encounters from that date onward.',
    },
];

export function ClaimScopeField() {
    return (
        <Form.Item name={['ehr', 'claimScope']} noStyle>
            <CardRadioGroup horizontal options={SCOPE_OPTIONS} />
        </Form.Item>
    );
}

export function ClaimStartFields() {
    const form = Form.useFormInstance();
    const claimStartMode = Form.useWatch(['ehr', 'claimStartMode'], form);
    return (
        <>
            <Form.Item name={['ehr', 'claimStartMode']} noStyle>
                <CardRadioGroup horizontal options={START_OPTIONS} />
            </Form.Item>
            {claimStartMode === 'specific' && (
                <Form.Item name={['ehr', 'claimStartDate']} label="Start date">
                    <DatePicker size="large" style={{ width: '100%' }} />
                </Form.Item>
            )}
        </>
    );
}

// Step 6 — Select EHR & billing scope：EHR 连接 + 患者范围 + 起始时间
export default function StepEhrScope() {
    const form = Form.useFormInstance();
    const watchedBillingEhrs: string[] | undefined = Form.useWatch(['ehr', 'billingEhrs'], form);
    const billingEhrs = useMemo(() => watchedBillingEhrs || [], [watchedBillingEhrs]);
    const [showPicker, setShowPicker] = useState(false);
    // 行内 Show steps / Reconnect 打开的单个 EHR 连接流程
    const [flowEhr, setFlowEhr] = useState<{ name: string; reconnect: boolean } | null>(null);
    // useEhrConnections 全局同步：弹窗里连接/断开/登记 custom 后，本实例列表自动跟上
    const [connections, connectionsLoading, refreshConnections, connectedNames] = useEhrConnections();
    const [ehrOptions] = useEhrOptions();

    // 自建/未收录 EHR（isCustom，长期 pending 等内部对接）：与已连接 EHR 一样入列并默认勾选
    // （后端口径：isCustom 也要写进 billingEhrs；适配器上线前 dispatcher 会跳过，不产 claim）
    const customNames = useMemo(() => connections.filter((c) => c.isCustom).map((c) => c.ehrSystem), [connections]);

    // callout 文案里的支持列表由 options 接口拼出（排除 upcoming）："A, B, and C"；接口未回时省略该句
    const supportedEhrText = useMemo(() => {
        const names = ehrOptions.filter((e) => !e.upcoming).map((e) => e.name);
        if (!names.length) return '';
        if (names.length === 1) return names[0];
        if (names.length === 2) return `${names[0]} and ${names[1]}`;
        return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
    }, [ehrOptions]);
    // 从未选择过 EHR 但已有连接时，默认选中已连接 + custom 的 EHR（仅初始化一次，用户 Remove 后不回填）
    const defaultedRef = useRef(false);
    useEffect(() => {
        if (defaultedRef.current || connectionsLoading) return;
        defaultedRef.current = true;
        const current: string[] = form.getFieldValue(['ehr', 'billingEhrs']) || [];
        const defaults = [...customNames, ...Array.from(connectedNames)];
        if (current.length === 0 && defaults.length > 0) {
            form.setFieldValue(['ehr', 'billingEhrs'], defaults);
        }
    }, [connectionsLoading, connectedNames, customNames, form]);

    // 连接成功 + custom 都入列，按后端返回顺序展示（custom 行琥珀 Not supported yet）；
    // billingEhrs 里已断开/失效的名字不展示，但仍留在表单值里随保存提交，由后端裁决
    // 原型 V3：custom 行同时标 Invite pending（提交时已确认加了 agent 邮箱）；PF 异步开通中也入列并标进度。
    // 验证没过就离开的也入列（原型 renderEHR：Agent not added + Show steps），连上过又断了的标 Connection lost
    const checkItems = useMemo(
        () =>
            connections
                .filter(
                    (c) =>
                        c.connectionStatus === 'connected' ||
                        c.isCustom ||
                        isPfSettingUp(c) ||
                        neverConnected(c) ||
                        connectionLost(c),
                )
                .map((c) => {
                    if (c.isCustom) {
                        return {
                            name: c.ehrSystem,
                            pending: true,
                            pendingTags: ['Not supported yet', 'Invite pending'],
                        };
                    }
                    if (c.connectionStatus !== 'connected' && isPfSettingUp(c)) {
                        return { name: c.ehrSystem, pending: true, pendingTags: [PF_SETUP_LABEL[c.setupStatus!]] };
                    }
                    const lost = connectionLost(c);
                    if (lost || neverConnected(c)) {
                        return {
                            name: c.ehrSystem,
                            warnTags: [lost ? 'Connection lost' : notConnectedCopy(c.ehrSystem).tag],
                            action: (
                                <Button
                                    type="link"
                                    className={sc.rowLink}
                                    onClick={() => setFlowEhr({ name: c.ehrSystem, reconnect: lost })}
                                >
                                    {lost ? 'Reconnect' : 'Show steps'}
                                </Button>
                            ),
                        };
                    }
                    return { name: c.ehrSystem };
                }),
        [connections],
    );
    // 只登记了未收录 EHR、没有任何已支持的连接：提示仍可完成设置，但在连上已支持的 EHR 前不会产生 claim
    const customOnly = useMemo(() => {
        const customNamesList = connections.filter((c) => c.isCustom).map((c) => c.ehrSystem);
        return customNamesList.length > 0 && customNamesList.length === checkItems.length ? customNamesList : [];
    }, [connections, checkItems]);

    // 新连接的 EHR 默认勾上（列表刷新由弹窗内部 refresh 广播过来）
    const handleEhrConnected = (ehrName: string) => {
        if (!billingEhrs.includes(ehrName)) {
            form.setFieldValue(['ehr', 'billingEhrs'], [...billingEhrs, ehrName]);
        }
        setShowPicker(false);
    };

    // 弹窗里断开后：后端会把该 EHR 自动移出 billingEhrs，重拉服务端勾选值回填即可
    const handleEhrDisconnected = async () => {
        const vo = await fetchOnboardingVo();
        if (vo) form.setFieldValue(['ehr', 'billingEhrs'], vo.claimSettings?.billingEhrs || []);
    };

    return (
        <>
            <div className={s.wizSection} data-wiz-section>
                <SectionHeader title="Which EHR has the patients you would like Klarity to bill for?" />
                <div className={s.callout}>
                    <InfoIcon />
                    <span>
                        Klarity will only process claims for patients in the EHR you select.
                        {supportedEhrText ? ` Currently supports ${supportedEhrText}.` : ''} You can finish setting up
                        without one, but Klarity cannot create claims until an EHR is connected.
                    </span>
                </div>
                {/* billingEhrs / otherEhr 只存值（注册字段供 useWatch 响应）；连接流程由 EHR picker 弹窗承接（本期不实现） */}
                <Form.Item name={['ehr', 'billingEhrs']} noStyle>
                    <HiddenFormField />
                </Form.Item>
                <Form.Item name={['ehr', 'otherEhr']} noStyle>
                    <HiddenFormField />
                </Form.Item>
                {checkItems.length > 0 ? (
                    <>
                        {/* 勾选 = 纳入计费范围（billingEhrs）；随步骤 Save 一起提交 */}
                        <EhrBillingCheckList
                            items={checkItems}
                            value={billingEhrs}
                            onChange={(next) => form.setFieldValue(['ehr', 'billingEhrs'], next)}
                        />
                        {customOnly.length > 0 && (
                            <div className={sc.customOnlyNote}>
                                We do not support {customOnly.join(', ')} yet. You can finish setting up now and we will
                                reach out about it. Klarity cannot create claims until one of your EHRs is connected.
                            </div>
                        )}
                        <Button className={s.addBtn} onClick={() => setShowPicker(true)}>
                            {customOnly.length > 0 ? '+ Connect a supported EHR instead' : '+ Connect another EHR'}
                        </Button>
                    </>
                ) : (
                    <Button type="primary" className={sc.ehrConnectBtn} onClick={() => setShowPicker(true)}>
                        Connect EHR
                    </Button>
                )}
                {flowEhr && (
                    <EhrConnectFlow
                        ehrName={flowEhr.name}
                        open
                        reconnect={flowEhr.reconnect}
                        onClose={() => setFlowEhr(null)}
                        onSuccess={() => {
                            refreshConnections();
                            handleEhrConnected(flowEhr.name);
                            setFlowEhr(null);
                        }}
                    />
                )}
                <EhrConnectModal
                    open={showPicker}
                    onClose={() => setShowPicker(false)}
                    onConnected={handleEhrConnected}
                    onDisconnected={handleEhrDisconnected}
                />
            </div>

            <div className={s.wizSection} data-wiz-section>
                <SectionHeader title="Which patients should Klarity bill for?" />
                <ClaimScopeField />
            </div>

            <div className={s.wizSection} data-wiz-section>
                <SectionHeader title="When should Klarity start processing claims?" />
                <ClaimStartFields />
            </div>
        </>
    );
}
